import { Box, TextField, Typography } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";

import { ApolloError } from "@apollo/client";
import { InputFieldError } from "../../../../styles/styledComponents";
import { OTPVerifyFormType } from "../../../../services/user-create/user-create.schema";

type Props = {
  onSubmit: (data: OTPVerifyFormType) => void;
  length: number;
  handleResendOtp: () => void;
  otpSentData: any;
  error? : ApolloError | undefined;
}

const OTPVerifyForm = (props: Props) => {
  const { onSubmit, length, handleResendOtp, otpSentData, error } = props;
  const otpBoxReference = useRef<any>([]);
  const [otp, setOTP] = useState<string[]>(new Array(length).fill(""));
  const [countdown, setCountdown] = useState<number>(60);
  const [resendOTPTrigger, setResendOTPTrigger] = useState(false);
  const [otpApiErrorState, setApiOtpErrorState] = useState(false);
  const timerRef = useRef<NodeJS.Timeout>();
  const [otpError, setOtpError] = useState<string | null>(null);

  const startTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    timerRef.current = setInterval(() => {
      setCountdown((prevCountdown) => {
        if (prevCountdown <= 1) {
          clearInterval(timerRef.current);

          return 0;
        }

        return prevCountdown - 1;
      });
    }, 1000);
  };


  function handleChange(value: string, index: number) {
    // For copying value from keyboard clipbaord
    if (value.trim().length > 1) {
      if (value.length === length) {
        setOTP(value.split(""));

        if (index < length - 1) {
          otpBoxReference.current[length - 1].focus();
        }
      } else if (value.length === 2) {
        otpBoxReference.current[index + 1].focus();
        const newArr = [...otp];

        newArr[index + 1] = value[1];
        setOTP(newArr);
      }

      return;
    }
    const newArr = [...otp];

    newArr[index] = value;
    setOTP(newArr);

    if (newArr[index] && index < length - 1) {
      otpBoxReference.current[index + 1].focus();
    }
  }

  function handleBackspaceAndEnter(e: any, index: number) {
    if (e.key === "Backspace" && index > 0) {
      otpBoxReference.current[index - 1].focus();
    }
    if (e.key === "Enter" && index < length - 1) {
      otpBoxReference.current[index + 1].focus();
    }
  }

  const handleFormSubmit = (e: any) => {
    e.preventDefault();
    onSubmit({ otp: otp.join("") });
  };

  useEffect(() => {
    if (otpSentData) {
      setOTP(new Array(length).fill(""));
    }
  }, [otpSentData]);

  function handlePaste(e: React.ClipboardEvent, index: number) {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text");
    const pastedArray = pastedData.split("");

    if (pastedArray.length > length) {
      return;
    }
    if (pastedArray.length === length) {
      setOTP(pastedArray);
    }
    if (index < length - 1) {
      const newArr = [...pastedArray];

      if (pastedArray.length < length) {
        newArr.push(...new Array(length - pastedArray.length).fill(""));
      }

      setOTP(newArr);
      if (pastedArray.length < length - 1) {
        otpBoxReference.current[pastedArray.length + 1].focus();
      } else {
        otpBoxReference.current[length - 1].focus();
      }
    }
  }

  function handleResend() {
    handleResendOtp();
    setCountdown(60);
    setResendOTPTrigger(!resendOTPTrigger);
    startTimer();
  }

  useEffect(() => {
    if (error) {
      setApiOtpErrorState(true);
    } else {
      setApiOtpErrorState(false);
    }
  }, [error]);

  useEffect(() => {
    const otpLength = otp.join("").length;

    if (otpLength < 6) {
      // to handle error case condition explicitly
      setApiOtpErrorState(false);
    }

    if (otpLength > 0 && otpLength <= 5) {
      setOtpError("Invalid OTP. Please enter a 6 digit OTP.");
    } else {
      setOtpError(null);
    }
    if (otpLength === 6) {
      onSubmit({ otp: otp.join("") });
    }
  }, [otp]);

  useEffect(() => {
    startTimer();

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  function getBorderColor() {
    const otpLength = otp.join("").length;

    if (otpLength < 6) return "#E8DFD7";
    if (otpApiErrorState) return "#D35633 !important";
    if (otpLength === 6) return "#459796 !important";

    return "#E8DFD7";
  }

  return (
    <form onSubmit={handleFormSubmit} className="w-full flex flex-col items-center">
      <div className="flex justify-center gap-4">
        {otp.map((digit, index) => (
          <TextField
            key={index}
            inputMode="tel"
            type="number"
            value={digit}
            sx={{
              "& .MuiInputBase-root": {
                padding: "8px 0",
                background: "#FFF",
                width: "2.625rem",
                maxWidth: "2.625rem",
              },
              "& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#E8DFD7",
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: getBorderColor(),
              },
              "& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: (theme)=>theme.palette.primary.main,
              },
            }}
            size="small"
            onChange={(e) => handleChange(e.target.value, index)}
            onKeyUp={(e) => handleBackspaceAndEnter(e, index)}
            onPaste={(e) => handlePaste(e, index)}
            inputProps={{ maxLength: 1 }}
            autoFocus={index === 0}
            inputRef={(reference: any) => (otpBoxReference.current[index] = reference)} />
        ))}
      </div>

      {otpError && <Box className="w-[80%] text-center">
        <InputFieldError
          severity="error"
        >
          {otpError}
        </InputFieldError>

      </Box>}

      <div className="flex justify-center cursor-pointer py-1 px-3 rounded-xl bg-taupe-15 hover:bg-taupe-50"
        onClick={countdown === 0 ? handleResend : () => { }}
      >
        <Typography variant="body1" fontFamily="geograph-regular" className="font-semibold !cursor-pointer">
          {countdown == 0 ? "Resend code" : `Resend code in ${countdown}s`}
        </Typography>
      </div>

      <Box sx={{ mt: "1.5rem" }} className="w-full">
        {/* <Button
          id="otpVerifyFormButton"
          color="primary"
          type="submit"
          className="!font-bold !capitalize"
          fullWidth
          disabled={otp.join("").length < 6}
        >
          Continue
        </Button> */}
      </Box>
    </form>
  );
};

export default OTPVerifyForm;
